/* eslint-disable @next/next/no-img-element */
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import React, { FC, ReactNode, useMemo, useState } from 'react'
import { isVideo } from '../../common/misc'
import { useChinaDetector } from '../../common/region-locale'
import { stripHtmlTagsAndDecode, stripHtmlTagsKeepLinks } from '../../common/rich-text-helper'
import PdImage from '../pd-image/pd-image'
import PdMedia from '../pd-media/pd-media'
import PdStandaloneVideo from '../pd-standalone-video/pd-standalone-video'

interface PdNewsProps {
  img: string
  imageAlt?: string
  title: string
  date?: string
  link?: string
  overview?: string | ReactNode
  mini?: boolean
  wfull?: boolean
  type?: 'news' | 'media' | 'blog' | 'event'
  imgClass?: string
}

export type NewsMeta = PdNewsProps

const PdNews: FC<PdNewsProps> = (props: PdNewsProps) => {
  const t = useTranslations()
  const inChina = useChinaDetector()
  const [playIcon, setPlayIcon] = useState<string>('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
  const [playingVideoSrc, setPlayingVideoSrc] = useState<string>('')
  const [playing, setPlaying] = useState<boolean>(false)

  function play(videoSrc: string) {
    setPlayingVideoSrc(videoSrc)
    setPlaying(true)
  }

  const pureOverview = useMemo(() => {
    if (props.overview && typeof props.overview === 'string') {
      return stripHtmlTagsAndDecode(props.overview)
    }
    return ''
  }, [props.overview])

  const anchorOverview = useMemo(() => {
    if (typeof props.overview === 'string' && props.overview) {
      return stripHtmlTagsKeepLinks(props.overview)
    }
    return ''
  }, [props.overview])

  return (
    <div
      className={[
        props.mini ? '' : 'laptop:flex-row',
        props.mini
          ? 'transition-all after:duration-300 after:transition-all after:absolute after:content-[""] after:w-0 after:border-b-4 hover:after:w-full hover:after:border-pdblue'
          : '',
        'relative w-full h-full flex laptop:justify-between flex-col-reverse',
      ].join(' ')}
    >
      <div
        className={[
          props.mini ? 'p-4 grow' : 'p-4 laptop:p-0',
          props.mini
            ? ''
            : props.wfull
              ? 'w-full'
              : 'w-auto -translate-y-2 z-10 p-4 mx-[1.5rem] laptop:translate-y-0 laptop:mx-0 laptop:p-0',
          'flex flex-col justify-between bg-white',
        ].join(' ')}
      >
        <div className={['shrink', 'pr-0', props.mini ? '' : 'laptop:pr-6'].join(' ')}>
          <Link href={props.link || '#'}>
            <h5
              className={[
                props.mini ? 'text-sm h-10' : 'text-lg laptop:text-2xl',
                'font-semibold line-clamp-2 cursor-pointer',
              ].join(' ')}
              title={props.title}
            >
              {props.title}
            </h5>
          </Link>
          {!props.mini && typeof props.overview === 'string' && (
            <div
              className="[&_a]:text-[#0066FF] mt-6 text-sm laptop:text-base line-clamp-9 laptop:line-clamp-8 laptop:h-55 h-180px overflow-hidden"
              style={{ color: 'rgba(0,0,0,.6)' }}
              title={pureOverview}
              dangerouslySetInnerHTML={{ __html: anchorOverview }}
            />
          )}
          {!props.mini && React.isValidElement(props.overview) && (
            <div className="mt-6 relative overflow-hidden">{props.overview}</div>
          )}
        </div>
        <div className="pt-8 flex">
          {props.date && <p className="text-sm text-black/60">{props.date}</p>}
          {props.type === 'news' && <p className="text-sm text-pdblue ml-5">{t('NewsReport')}</p>}
          {props.type === 'blog' && <p className="text-sm text-pdblue ml-5">{'Blogs'}</p>}
          {props.type === 'event' && <p className="text-sm text-pdblue ml-5">{'Event'}</p>}
          {/* {!inChina &&
            (props.type === 'media' || props.link?.startsWith('http://') || props.link?.startsWith('https://')) && (
              <p className="text-sm text-pdblue ml-5">{t('MediaCoverage')}</p>
            )} */}
        </div>
      </div>

      <div
        className={[
          props.mini ? 'laptop:h-46 h-209px' : 'laptop:w-148 laptop:h-94 h-60',
          props.imgClass ? props.imgClass : '',
          'w-full flex-none overflow-hidden',
        ].join(' ')}
      >
        {isVideo(props.img) ? (
          <PdMedia src={props.img} mask={isVideo(props.img)} stopAutoplay={true} linkHref={props.link}>
            {isVideo(props.img) && (
              <img
                src={playIcon}
                className="absolute block w-12 h-12 transition-all hover:scale-116 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                alt="play"
                onMouseEnter={() =>
                  setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play_hover.svg')
                }
                onMouseLeave={() => setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')}
                onClick={(e) => {
                  e.preventDefault()
                  play(props.img)
                }}
              />
            )}
          </PdMedia>
        ) : (
          <PdImage
            src={props.img}
            alt={props.imageAlt ?? props.title}
            object="cover"
            objectPosition="top"
            enableMask={false}
            width={'100%'}
            height={'100%'}
            linkHref={props.link}
          />
        )}
      </div>
      <PdStandaloneVideo source={playingVideoSrc} show={playing} onClosed={() => setPlaying(false)} />
    </div>
  )
}

export default PdNews
